import i18next from 'i18next';
import { call, put, takeLatest } from 'redux-saga/effects';

import { apiLoadUserModel } from 'src/api/account/profile';
import { convertToClientUserModel } from 'src/models/Account/User';
import { Enums } from 'src/models/enums';
import { prepareLeftMenuModel } from 'src/models/Partner/Sidebar';
import { ROUTE_SIGNIN_PATH } from 'src/routes';
import { baseSaga } from 'src/store/baseSaga';
import { checkCanCreateCampaignFromTemplate, checkCustomerType } from 'src/store/commonPart/sagas';
import { commonSlice } from 'src/store/commonPart/slice';
import { currentUserSlice } from 'src/store/user/slice';
import { customHistory } from 'src/utils/customHistory';
import { GoogleTagManager, GTMAdditionalAttributesType } from 'src/utils/GoogleTagManager';
import { safeRedirect } from 'src/utils/hardRedirect';
import { isHttpResponseValid } from 'src/utils/httpResponse';
import { eventPublishAuthChanged } from 'src/utils/outer';
import { identifyFullstory } from 'src/utils/fullstory';
import { PromiseReturnType } from 'src/utils/types';
import { addReturnUrl } from 'src/utils/url';
import { store } from 'src/store/store';
import { PayloadAction } from '@reduxjs/toolkit';

function* getCurrentUserSaga(): Generator {
  yield baseSaga(function* () {
    const response: PromiseReturnType<typeof apiLoadUserModel> = yield call(apiLoadUserModel);

    if (isHttpResponseValid(response)) {
      eventPublishAuthChanged(response.UserModel);
      identifyFullstory(response.UserModel);

      /** if id = 0, it's not an authorized user */
      const isAuthorizedUser = response.UserModel.ID;

      if (isAuthorizedUser) {
        /** gtm event */
        (() => {
          const gtmEvent: GTMAdditionalAttributesType = {
            user_id: response.UserModel.ID,
            first_name: response.UserModel.FirstName,
            last_name: response.UserModel.LastName,
            email: response.UserModel.Email,
            created_at: response.UserModel.CreatedDate,
          };

          if (response.UserModel.OrganizationUserRole) {
            gtmEvent.user_role = i18next.t(
              `common.roles.${
                // eslint-disable-next-line no-nested-ternary
                response.UserModel.OrganizationUserRole === Enums.organizations.StaffRoleCode.Administrator
                  ? 'administrator'
                  : response.UserModel.OrganizationUserRole === Enums.organizations.StaffRoleCode.Advocate
                  ? 'advocate'
                  : 'staff'
              }`
            );
          }

          if (response.UserModel.OrganizationInfo) {
            gtmEvent.org_name = response.UserModel.OrganizationInfo.CommonName;
            gtmEvent.org_id = response.UserModel.OrganizationInfo.OrganizationIdString;
            gtmEvent.org_country = response.UserModel.OrganizationInfo.CountryCode;
            gtmEvent.org_501c = response.UserModel.OrganizationInfo.Is501C;
            gtmEvent.org_state = response.UserModel.OrganizationInfo.RegionCode;
            gtmEvent.org_stripe_account_connected = response.UserModel.OrganizationInfo.IsStripeConfigured;
            gtmEvent.amount_raised_online_annually = response.UserModel.OrganizationInfo.AnnualRaiseLevel;
          }

          GoogleTagManager.pushEvent(gtmEvent);
        })();
        /** end gtm event */

        yield call(checkCanCreateCampaignFromTemplate);
        yield call(checkCustomerType);
      }

      yield put(
        currentUserSlice.actions.getCurrentUserOk(
          isAuthorizedUser ? convertToClientUserModel(response.UserModel) : null
        )
      );

      yield put(
        commonSlice.actions.setOrganizationSubscriptionType(response.LeftMenuModel.OrganizationSubscriptionType)
      );

      const partnerName: string = store.getState().common.currentPartner.response?.Name || '';

      yield put(
        commonSlice.actions.setSidebarModel(
          isAuthorizedUser
            ? prepareLeftMenuModel(
                response.LeftMenuModel,
                response.UserModel.OrganizationInfo.OrganizationUrl,
                partnerName
              )
            : null
        )
      );

      yield put(commonSlice.actions.setGlobalNavigationModel(isAuthorizedUser ? response.LeftMenuModel : null));
    }
  }, currentUserSlice.actions.getCurrentUserError);
}

function* clearCurrentUserSaga({ payload }: PayloadAction<boolean>): Generator {
  if (payload && customHistory.location.pathname !== ROUTE_SIGNIN_PATH) {
    // window variables break the login page (uncomment when it's ready for SPA)
    // customHistory.push(`${ROUTE_SIGNIN_PATH}?returnUrl=${customHistory.location.pathname}`);
    // setVerificationToken();

    safeRedirect(addReturnUrl(ROUTE_SIGNIN_PATH, customHistory.location.pathname));
  }
  yield;
}

export function* userSagas(): Generator {
  yield call(getCurrentUserSaga);

  yield takeLatest(currentUserSlice.actions.getCurrentUserStart.type, getCurrentUserSaga);
  yield takeLatest(currentUserSlice.actions.clearCurrentUser.type, clearCurrentUserSaga);
}
